import React from "react";
import {
  Box,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ResourcePicker from "./ResourcePicker";

/**
 * Dynamic input renderer driven by a playbook's PlaybookInputSchema[].
 *
 * Type → control:
 *   string       → TextField
 *   boolean      → Switch
 *   enum         → Select with enum_values
 *   integer      → number TextField
 *   resource_ref → ResourcePicker (autocomplete backed by /inventory)
 */
const PlaybookInputs = ({
  inputs = [],
  values = {},
  errors = {},
  onChange,
  disabled = false,
  accountNumber = "",
}) => {
  if (!inputs || inputs.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        This check has no additional inputs.
      </Typography>
    );
  }

  const set = (name, val) => onChange({ ...values, [name]: val });

  return (
    <Box>
      {inputs.map((input) => {
        const cur = values[input.name] ?? input.default ?? (input.type === "boolean" ? false : "");
        const error = errors[input.name];

        if (input.type === "boolean") {
          return (
            <Box key={input.name} sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={Boolean(cur)}
                    onChange={(e) => set(input.name, e.target.checked)}
                    disabled={disabled}
                  />
                }
                label={
                  <>
                    {input.name.replace(/_/g, " ")}
                    {input.required && <span style={{ color: "#c62828" }}> *</span>}
                  </>
                }
              />
              {input.description && (
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", ml: 4 }}>
                  {input.description}
                </Typography>
              )}
              {error && (
                <Typography variant="caption" sx={{ display: "block", color: "error.main", ml: 4 }}>
                  {error}
                </Typography>
              )}
            </Box>
          );
        }

        if (input.type === "enum") {
          return (
            <TextField
              key={input.name}
              size="small"
              select
              fullWidth
              required={input.required}
              label={input.name.replace(/_/g, " ")}
              helperText={error || input.description}
              error={Boolean(error)}
              value={cur || ""}
              onChange={(e) => set(input.name, e.target.value)}
              disabled={disabled}
              sx={{ mb: 2 }}
            >
              {(input.enum_values || []).map((v) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </TextField>
          );
        }

        if (input.type === "integer") {
          return (
            <TextField
              key={input.name}
              size="small"
              type="number"
              fullWidth
              required={input.required}
              label={input.name.replace(/_/g, " ")}
              helperText={error || input.description}
              error={Boolean(error)}
              value={cur ?? ""}
              onChange={(e) => set(input.name, e.target.value === "" ? "" : Number(e.target.value))}
              disabled={disabled}
              sx={{ mb: 2 }}
            />
          );
        }

        if (input.type === "resource_ref") {
          return (
            <ResourcePicker
              key={input.name}
              inventoryType={input.inventory_type}
              accountNumber={accountNumber}
              label={input.name.replace(/_/g, " ")}
              description={input.description}
              required={input.required}
              disabled={disabled}
              value={cur || ""}
              onChange={(v) => set(input.name, v)}
              error={error}
            />
          );
        }

        // string fallback
        return (
          <TextField
            key={input.name}
            size="small"
            fullWidth
            required={input.required}
            label={input.name.replace(/_/g, " ")}
            helperText={error || input.description}
            error={Boolean(error)}
            value={cur || ""}
            onChange={(e) => set(input.name, e.target.value)}
            disabled={disabled}
            sx={{ mb: 2 }}
          />
        );
      })}
    </Box>
  );
};

export default PlaybookInputs;
